import { PublicKey } from "@solana/web3.js";
import bs58 from "bs58";

/* ============================================================================
 * TYPES
 * ========================================================================== */

export type PubkeyInput = string | PublicKey;

/* ============================================================================
 * KEY MANAGER
 * ========================================================================== */

class KeyManager {
  private cache = new Map<string, PublicKey>();

  /* ------------------------------
   * Guards
   * ------------------------------ */

  isPubkey(obj: unknown): obj is PublicKey {
    return obj instanceof PublicKey;
  }

  isValidBase58(str: unknown): str is string {
    if (typeof str !== "string") return false;
    try {
      bs58.decode(str);
      return true;
    } catch {
      return false;
    }
  }

  /* ------------------------------
   * Core API
   * ------------------------------ */

  getPubkey(input: PubkeyInput): PublicKey {
    if (this.isPubkey(input)) {
      return input;
    }

    if (!this.isValidBase58(input)) {
      throw new Error(`Invalid Base58 public key: ${String(input)}`);
    }

    const cached = this.cache.get(input);
    if (cached) return cached;

    const pubkey = new PublicKey(input);
    this.cache.set(input, pubkey);
    return pubkey;
  }

  getPubkeyString(input: PubkeyInput): string {
    return this.getPubkey(input).toBase58();
  }

  clearCache(): void {
    this.cache.clear();
  }
}

/* ============================================================================
 * SINGLETON
 * ========================================================================== */

export const keyManager = new KeyManager();

/* ============================================================================
 * FUNCTIONAL WRAPPERS (OPTIONAL)
 * ========================================================================== */

export const isPubkey = (v: unknown): v is PublicKey =>
  keyManager.isPubkey(v);

export const isValidBase58 = (v: unknown): v is string =>
  keyManager.isValidBase58(v);

export const getPubkey = (v: PubkeyInput): PublicKey =>
  keyManager.getPubkey(v);

export const getPubkeyString = (v: PubkeyInput): string =>
  keyManager.getPubkeyString(v);
